import React from 'react';
import { Pressable, View } from 'react-native';
import { useAppStore } from '@restorant/core';
import type { StringKey, ViewId } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useT } from '../i18n';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';
import { Screen } from '../components/Screen';
import { T } from './../components/Text';

export interface MoreEntry {
  id: string;
  labelKey: string;
  icon: string;
}

/**
 * One row in the More list. Deliberately a tall, full-width target: this
 * replaces a tab that was previously one tap away, so it has to be at least as
 * easy to hit with a thumb as the bar it came from.
 */
function MoreRow({ entry }: { entry: MoreEntry }) {
  const colors = useColors();
  const t = useT();
  const setView = useAppStore((s) => s.setView);

  return (
    <Pressable
      testID={`more-${entry.id}`}
      onPress={() => setView(entry.id as ViewId)}
      style={({ hovered, pressed }: any) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        paddingVertical: 16,
        paddingHorizontal: 16,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: hovered ? colors.goldDim : colors.line,
        backgroundColor: pressed ? colors.surface2 : colors.surface,
        marginBottom: 10,
      })}
    >
      <View
        style={{
          width: 38,
          height: 38,
          borderRadius: radius.md,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.surface2,
          borderWidth: 1,
          borderColor: colors.line,
        }}
      >
        <Icon name={entry.icon as IconName} size={18} color={colors.gold} />
      </View>
      <T size={15} weight={700} style={{ flex: 1 }} numberOfLines={1}>
        {t(entry.labelKey as StringKey)}
      </T>
      <Icon name="chevronRight" size={17} color={colors.textFaint} />
    </Pressable>
  );
}

/**
 * Phone only. The tabs that did not fit in the bottom bar.
 *
 * The list is handed in rather than computed here, because which tabs exist at
 * all is a role question that App.tsx has already answered — an owner's list
 * includes Expenses, a worker's does not, and this screen should not be a
 * second place where that decision is made and could drift.
 */
export function MoreScreen({
  contentPadding,
  entries,
}: {
  contentPadding: number;
  entries: MoreEntry[];
}) {
  return (
    <Screen contentPadding={contentPadding}>
      {entries.map((e) => (
        <MoreRow key={e.id} entry={e} />
      ))}
    </Screen>
  );
}
